import React, { useState } from 'react';
import { Calendar, Clock, X } from 'lucide-react';
import Button from './ui/button';
import Input from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

const ScheduleModal = ({ isOpen, onClose, onSchedule, runData }) => {
  const [formData, setFormData] = useState({
    frequency: 'daily',
    time: '09:00',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    enabled: true,
    day_of_week: 1, // Monday
    day_of_month: 1,
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await onSchedule(formData);
      onClose();
    } catch (error) {
      console.error('Failed to schedule:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="max-w-md w-full">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center space-x-2">
              <Calendar className="w-5 h-5" />
              <span>Schedule Recurring Evaluation</span>
            </CardTitle>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Evaluation Info */}
            <div className="p-3 bg-secondary/50 rounded-lg">
              <p className="text-sm text-muted-foreground">Evaluation</p>
              <p className="font-medium">{runData?.evaluation_name}</p>
              <p className="text-sm text-muted-foreground">
                Model: {runData?.model} | Provider: {runData?.provider}
              </p>
            </div>

            {/* Frequency */}
            <div>
              <label className="block text-sm font-medium mb-2">Frequency</label>
              <select
                value={formData.frequency}
                onChange={(e) => handleChange('frequency', e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-md bg-background"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>

            {/* Day of Week (for weekly) */}
            {formData.frequency === 'weekly' && (
              <div>
                <label className="block text-sm font-medium mb-2">Day of Week</label>
                <select
                  value={formData.day_of_week}
                  onChange={(e) => handleChange('day_of_week', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-input rounded-md bg-background"
                >
                  <option value={0}>Sunday</option>
                  <option value={1}>Monday</option>
                  <option value={2}>Tuesday</option>
                  <option value={3}>Wednesday</option>
                  <option value={4}>Thursday</option>
                  <option value={5}>Friday</option>
                  <option value={6}>Saturday</option>
                </select>
              </div>
            )}

            {/* Day of Month (for monthly) */}
            {formData.frequency === 'monthly' && (
              <div>
                <label className="block text-sm font-medium mb-2">Day of Month</label>
                <Input
                  type="number"
                  min="1"
                  max="31"
                  value={formData.day_of_month}
                  onChange={(e) => handleChange('day_of_month', parseInt(e.target.value))}
                />
              </div>
            )}

            {/* Time */}
            <div>
              <label className="block text-sm font-medium mb-2">
                <Clock className="w-4 h-4 inline mr-1" />
                Time
              </label>
              <Input
                type="time"
                value={formData.time}
                onChange={(e) => handleChange('time', e.target.value)}
              />
            </div>

            {/* Timezone */}
            <div>
              <label className="block text-sm font-medium mb-2">Timezone</label>
              <Input
                type="text"
                value={formData.timezone}
                onChange={(e) => handleChange('timezone', e.target.value)}
                placeholder="e.g., America/New_York"
              />
            </div>

            {/* Enabled */}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="enabled"
                checked={formData.enabled}
                onChange={(e) => handleChange('enabled', e.target.checked)}
                className="rounded border-input"
              />
              <label htmlFor="enabled" className="text-sm">
                Enable schedule immediately
              </label>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Scheduling...' : 'Schedule Evaluation'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ScheduleModal;
